import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import Context from '@context/store-finder';
import TypeSet from '../../base_typeset/component';
import Icon from '../../base_svg-icon/component';
import { Button } from 'audamatic-ui';
import Restapi from '@utils/api';
import store from '@utils/store';
import { getDevice, miniMustache, capitalize, getFeature, isValidDate, strippedPhoneNumber, openMaps } from '@utils';
import Text from '@text';
import { t } from '@utils/i18n';

import './styles/styles.scss';


const  Location = (props) => {
  const {details, now, showStock} = props;
  const context = useContext(Context);
  const api = new Restapi();
  const locationRef = useRef(null);

  const [showHours, setShowHours] = useState(false);
  const [sundayOpen, setSundayOpen] = useState(true);

  /**
   * Open/close the opening hours panel
   * if on mobile, scroll to the bottom of this component
   */
  const toggleOpeningHours = () => {
    setShowHours(prev => !prev);
    const { isMobile } = getDevice();

    if (isMobile && !context.listView && context.activeStore?.storeId) {
      locationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  };

  /**
   * Format opening hours. If isSunday is set,
   * check if it is closed and should get a different label
   * @param {string} hours
   * @param {boolean} isSunday
   */
  const formatHours = (hours = '', isSunday) => {
    let label = hours;
    label = label.replace('uur', '');

    if (isSunday) {
      const regex = /\d{1,2}[\.\:]\d{1,2}.{1,3}\-.{1,3}\d{1,2}[\.\:]\d{1,2}/g;
      if (!hours.match(regex)) {
        label = t('storefinder.location.hours.unknown');
        if (!hours || hours.toLowerCase() === 'gesloten') {
          label = t('storefinder.location.hours.closed');
        }
      }
      if (sundayOpen) {
        setSundayOpen(false );
      }
    }

    return label;
  };

  /**
   * Overcomplicated getter to check the opening hours for each day of the week
   * Opening hours are manually provided, so they can differ, hence all the regex's
   *
   * @returns {object} { open, list, status label }
   */
  const openingHours = () => {
    const {
      openMonday,
      openTuesday,
      openWednesday,
      openThursday,
      openFriday,
      openSaturday,
      openSunday,
    } = details.attributes || {};

    const date = now ? new Date(now) : new Date();
    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;

    const list = [
      {
        name: t('storefinder.location.hours.monday'),
        abbr: t('storefinder.location.hours.monday.abbr'),
        hours: formatHours(openMonday),
        hasPassed: (dayIndex === 0) ? null : (dayIndex > 0),
      }, {
        name: t('storefinder.location.hours.tuesday'),
        abbr: t('storefinder.location.hours.tuesday.abbr'),
        hours: formatHours(openTuesday),
        hasPassed: (dayIndex === 1) ? null : (dayIndex > 1),
      }, {
        name: t('storefinder.location.hours.wednesday'),
        abbr: t('storefinder.location.hours.wednesday.abbr'),
        hours: formatHours(openWednesday),
        hasPassed: (dayIndex === 2) ? null : (dayIndex > 2),
      }, {
        name: t('storefinder.location.hours.thursday'),
        abbr: t('storefinder.location.hours.thursday.abbr'),
        hours: formatHours(openThursday),
        hasPassed: (dayIndex === 3) ? null : (dayIndex > 3),
      }, {
        name: t('storefinder.location.hours.friday'),
        abbr: t('storefinder.location.hours.friday.abbr'),
        hours: formatHours(openFriday),
        hasPassed: (dayIndex === 4) ? null : (dayIndex > 4),
      }, {
        name: t('storefinder.location.hours.saturday'),
        abbr: t('storefinder.location.hours.saturday.abbr'),
        hours: formatHours(openSaturday),
        hasPassed: (dayIndex === 5) ? null : (dayIndex > 5),
      }, {
        name: t('storefinder.location.hours.sunday'),
        abbr: t('storefinder.location.hours.sunday.abbr'),
        hours: formatHours(openSunday, true),
        hasPassed: (dayIndex === 6) ? null : false,
      },
    ];

    let open = true;
    let label = list[dayIndex].hours;
    const [openingTime, closingTime] = label.match(/\d{1,}(\.|\:)\d{1,}/g) || [];

    /*
     * If closing time is not set (sunday) ro closing time has already passed today
     * we let the user know what tim it pens on the next day. If the opening time is set
     * but has not yet passed today, we tell the user what time is opens today.
     */
    const [closingHour, closingMins] = closingTime ? (closingTime.match(/\d{1,}/g) || []) : [];
    const [openingHour, OpeningMins] = openingTime ? (openingTime.match(/\d{1,}/g) || []) : [];

    // convert openinghours to Date instances to compare against current date instance
    const closingTimestamp = new Date();
    closingTimestamp.setHours(closingHour, closingMins);

    const openingTimestamp = new Date();
    openingTimestamp.setHours(openingHour, OpeningMins);

    const AlreadyClosed = isValidDate(closingTimestamp) ? closingTimestamp < date : true;
    const notOpenYet = isValidDate(openingTimestamp) ? openingTimestamp > date : true;

    if (!closingTime || AlreadyClosed) {
      const nextDayIndex = (dayIndex === 6) ? 0 : (dayIndex + 1);
      let nextDay = list[nextDayIndex];
      let [openingTimeTomorrow] = nextDay.hours.match(/\d{1,}(\.|\:)\d{1,}/g) || [];

      if (!openingTimeTomorrow) {
        nextDay = list[0];
        const timeFromString = nextDay.hours.match(/\d{1,}(\.|\:)\d{1,}/g);
        openingTimeTomorrow = timeFromString ? timeFromString[0] : '-';
      }

      label =  miniMustache({
        string: t('storefinder.location.hours.opensToday'),
        replacements: { abbr: nextDay.abbr,  time: openingTimeTomorrow },
      });
      open = false;
    } else if (openingTime && notOpenYet) {
      label = miniMustache({
        string: t('storefinder.location.hours.opensTomorrow'),
        replacements: { time: openingTime },
      });
      open = false;
    }

    return { list, label, open };
  }

  /**
   * Render status label based on whether it's open or not
   */
  const renderStatusLabel = () => {
    const { open, label } = openingHours();
    return (
      <div className="location-status-label">
        <TypeSet color={open ? 'success' : 'error'} bold tight>
          {t(open ? 'storefinder.location.hours.open' : 'storefinder.location.hours.closed')}
        </TypeSet>
        <TypeSet color="grey" tight>
          <div className="sep">|</div>
        </TypeSet>
        <TypeSet tight>{label}</TypeSet>
      </div>
    );
  };

  const stockLevel = () => {
    const { product = {} } = context;
    const { stock } = details;
    const levels = {
      unknown: { class: 'unknown', label: 'stock.status.unknown', hasIcon: false },
      limited: { class: 'limited', label: 'stock.status.limitedStock', hasIcon: true },
      inStock: { class: 'in-stock', label: 'stock.status.inStock', hasIcon: true },
      noStock: { class: 'no-stock', label: 'stock.status.noStock', hasIcon: false },
    };

    // Check if details.stockCheck or details.stockCheckQuantore is true, depending on the product.stockCheck attr.
    const stockCheck = details[product?.stockCheck];

    let properties = levels.unknown;
    if (stock <= 0) {
      properties = levels.noStock;
    }
    if (stock > 0) {
      properties = levels.limited;
    }
    if (stock > 1) {
      properties = levels.inStock;
    }
    if (!stockCheck) {
      properties = levels.unknown;
    }

    return (
      <div className={`stock-level ${properties.class}`}>
        {properties.hasIcon && (<Icon size={150} name="ico-check-verified"/>)}
        <TypeSet tag="div" bold>
          <Text>{properties.label}</Text>
        </TypeSet>
      </div>
    );
  }

  /**
   * Click & Collect orders are sent to a store's email address, which starts with the store ID.
   * The store ID in the email address is always n characters long, even when it's not.
   * So, the storeId can be 579, but the prefix would be '00579'. The number of leading zero's is
   * determined by the 'prefixLength' setting (bruna 4, ReadShop 5).
   * e.g. 0579kassamailbruna@brunawinkel.nl
   */
  const emailPrefix = () => {
    const { prefixLength = 0 } = getFeature('clickAndCollect');
    const { storeId = 0 } = details || {};
    const idArray = storeId
      .toString()
      .split(/(?!$)/u)
      .reverse();

    const prefixArray = [];
    for (let i = 0; i < prefixLength; i++) {
      prefixArray.push(idArray[i] || '0');
    }
    return prefixArray.reverse().join('');
  }

  /**
 * Sets the preferred store for the current customer.
 *
 * Behaviour:
 * 1. If the user is logged out, the provided `storeId` is cached
 *    in sessionStorage.
 * 2. If the user already has a preferred StoreId, no action is taken.
 * 3. Otherwise, a new preferred store is assigned to the customer.
 */
  const assignPreferredStoreToCustomer = async(storeId) => {
    if(!store.getBasket().user) {
      return sessionStorage.setItem('selectedStoreId', storeId);
    }
    if(!!store.getBasket().user.preferredStoreId){
      return
    }
     return api.assignPreferredStoreToCustomer(storeId)
      .then(() => store.loadBasket());
  }

  const startCollect = () => {
    const { city, address, phoneBusiness, valkStoreId, storeId } = details || {};
    const { setCollectInfo, collectInfo, setShowCollectForm } = context;

    assignPreferredStoreToCustomer(storeId);
    setShowCollectForm(true);
    setCollectInfo({
      ...collectInfo,
      address: `${capitalize(city)}, ${address}`,
      phoneBusiness,
      storeId: valkStoreId,
      intershopStoreId: parseInt(storeId, 10),
      emailPrefix: emailPrefix,
    });
  };

    const { tempStoreId, selectMode, onSelect, collect, product = {} } = context;
    const { stock } = details || {};
    const { address, postalCode, attributes = {}, phoneBusiness, deactivate = ()=> {}, toggleBounce = () => {}, distance, storeId, city, whatsapp } = details;
    const { latitude = 0, longitude = 0 } = attributes;

    // Check if details.stockCheck or details.stockCheckQuantore is true, depending on the product.stockCheck attr.
    const stockCheck = details[product?.stockCheck];
    const hasStock = stockCheck && stock > 0;

    if (!storeId) {
      return null;
    }

    return (
      <div
        ref={locationRef}
        className={`store-location ${tempStoreId === storeId ? 'active' : 'inactive'}`}
        onMouseEnter={toggleBounce}
        onMouseLeave={deactivate}
        data-test-id="storeLocation"
      >
        <div className="location-info">
          {showStock ? (
            <div className="minimal-info">
              <div className="location-details">
                <div className="location-title">
                  <TypeSet bold tight>{capitalize(city)}</TypeSet>
                </div>
                <div>
                  {!!distance && (
                    <TypeSet color="grey" tight>{Math.round(distance * 10) / 10} km | &nbsp;</TypeSet>
                  )}
                  <TypeSet tight><span data-test-id="storefinder-address">{address}</span></TypeSet>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="location-title">
                <TypeSet bold tight>{capitalize(city)}</TypeSet>
                {!!distance && (
                  <TypeSet color="grey" tight>{Math.round(distance * 10) / 10} km</TypeSet>
                )}
              </div>
              <TypeSet tag="div" tight>
                <span data-test-id="storefinder-address">{address}</span><br/>
                {postalCode} {capitalize(city)}
              </TypeSet>
            </>
          )}

          <div className="location-status" onClick={toggleOpeningHours} data-test-id="locationStatus">
            {renderStatusLabel()}
            <Icon size={87} name={`ico-arrow-${showHours ? 'up' : 'down'}`}/>
          </div>

          {showHours && (
            <table className="location-opening-hours" data-test-id="locationOpeningHours">
              <tbody>
                {openingHours().list.map((day) => {
                  let bold = false;
                  let color = (day.hasPassed) ? 'grey' : '';

                  if (day.hasPassed === null) {
                    color = '';
                    bold = true;
                  }

                  return (
                    <tr key={day.name}>
                      <td>
                        <TypeSet color={color} bold={bold} tight>{day.name}</TypeSet>
                      </td>
                      <td>
                        <TypeSet color={color} bold={bold} tight>{day.hours}</TypeSet>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div className="location-contact">
            {whatsapp && (
              <a
                href={`https://wa.me/${strippedPhoneNumber({ number: whatsapp })}`}
                target="_blank"
                rel="noreferrer"
              >
                <Icon size={100} name="ico-whatsapp"/> <Text>service.chat.title</Text>
              </a>
            )}

            <a href={`tel:${strippedPhoneNumber({ number: phoneBusiness })}`}>
              <Icon size={100} name="ico-telephone-circle"/> {strippedPhoneNumber({ number: phoneBusiness, pretty: true })}
            </a>
            <div onClick={openMaps(latitude, longitude)}>
              <Icon size={100} name="ico-navigation"/>
              <Text>storefinder.location.directions</Text>
            </div>
          </div>

          {selectMode && (
            <Button
              variant="primary"
              size="large"
              onClick={() => onSelect(storeId)}
              data-test-id="button-selectStore"
            >
              <Text>storefinder.location.select</Text>
            </Button>
          )}
        </div>

        {collect && (
          <div className="side-details">
            {stockLevel()}
            {getFeature('clickAndCollect').isActive && hasStock && (
              <Button
                variant="secondary"
                size="small"
                onClick={startCollect}
                data-test-id="button-selectStorePickup"
              >
                <Text>collect.button</Text>
              </Button>
            )}
          </div>
        )}

      </div>
    );
  }


Location.propTypes = {
  details: PropTypes.object,
  now: PropTypes.object,
  showStock: PropTypes.bool,
};

export default Location;
